import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FieldTimeOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Col, Row, Tag, Tooltip } from "antd";
import { observer } from "mobx-react";
import { formatAmount } from "../../../../../utils/formatAmount.js";
import { __ } from "../../../../../utils/i18n.js";
// Affiliate commissions are settled in EUR; the entity carries no per-record currency.
const AFFILIATE_CURRENCY = "EUR";
// Long-form dates follow the site language (document lang), not the browser's.
const formatDateLong = (date)=>new Intl.DateTimeFormat(document.documentElement.lang, {
        month: "long",
        day: "numeric"
    }).format(date);
const formatMonthLong = (date)=>new Intl.DateTimeFormat(document.documentElement.lang, {
        month: "long",
        year: "numeric"
    }).format(date);
const StatTile = ({ label, value, tooltip })=>/*#__PURE__*/ _jsxs("div", {
        className: "rc-account-v2-affiliate-stat",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-affiliate-stat__label",
                children: [
                    label,
                    tooltip && /*#__PURE__*/ _jsx(Tooltip, {
                        title: tooltip,
                        children: /*#__PURE__*/ _jsx(InfoCircleOutlined, {
                            className: "rc-account-v2-affiliate-stat__info"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-affiliate-stat__value",
                children: value
            })
        ]
    });
const AffiliateStatStrip = observer(({ affiliate })=>{
    const { commissions } = affiliate;
    // While the application is still under manual review a dedicated status tile
    // is shown (4 tiles). Otherwise the strip is three tiles — the former
    // "Next payout" tile was folded into the unpaid-commission tooltip below.
    const isReviewPending = affiliate.isManuallyReviewed === false;
    const colSpan = isReviewPending ? 6 : 8;
    const payoutBase = __("Paid out on the 15th if above your threshold.");
    const unpaidTooltip = isReviewPending ? payoutBase : affiliate.isOnTrackForNextPayout ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            payoutBase,
            " ",
            __("You are on track for the next payout on %s.", formatDateLong(affiliate.nextPayoutDate))
        ]
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            payoutBase,
            " ",
            __("%s left until your next payout threshold.", formatAmount({
                amount: affiliate.amountUntilThreshold,
                currency: AFFILIATE_CURRENCY,
                taxTreatment: "net"
            }))
        ]
    });
    return /*#__PURE__*/ _jsxs(Row, {
        gutter: [
            16,
            16
        ],
        className: "rc-account-v2-affiliate-stats",
        children: [
            isReviewPending && /*#__PURE__*/ _jsx(Col, {
                xs: 12,
                md: 6,
                children: /*#__PURE__*/ _jsx(StatTile, {
                    label: __("Affiliate status"),
                    value: /*#__PURE__*/ _jsx(Tag, {
                        icon: /*#__PURE__*/ _jsx(FieldTimeOutlined, {}),
                        children: __("Review pending")
                    }),
                    tooltip: __("We're checking your application - usually within 3 business days.")
                })
            }),
            /*#__PURE__*/ _jsx(Col, {
                xs: 12,
                md: colSpan,
                children: /*#__PURE__*/ _jsx(StatTile, {
                    label: __("Unpaid commission"),
                    value: formatAmount({
                        amount: commissions.totalUnpaid,
                        currency: AFFILIATE_CURRENCY,
                        taxTreatment: "net"
                    }),
                    tooltip: unpaidTooltip
                })
            }),
            /*#__PURE__*/ _jsx(Col, {
                xs: 12,
                md: colSpan,
                children: /*#__PURE__*/ _jsx(StatTile, {
                    label: __("Commission rate"),
                    value: `${affiliate.affiliate.commission} %`,
                    tooltip: affiliate.commissionPlanned !== undefined && affiliate.commissionPlannedChangeDate ? __("Becomes %1$d %% on %2$s.", affiliate.commissionPlanned, formatDateLong(affiliate.commissionPlannedChangeDate)) : undefined
                })
            }),
            /*#__PURE__*/ _jsx(Col, {
                xs: 12,
                md: colSpan,
                children: /*#__PURE__*/ _jsx(StatTile, {
                    label: __("Total paid commission"),
                    value: formatAmount({
                        amount: commissions.totalPaid,
                        currency: AFFILIATE_CURRENCY,
                        taxTreatment: "net"
                    }),
                    tooltip: affiliate.firstPayoutMonth ? __("Since your first payout in %s.", formatMonthLong(affiliate.firstPayoutMonth)) : undefined
                })
            })
        ]
    });
});
export { AffiliateStatStrip };
